// import { useState } from "react"; // Not used in this hook
import apiService from "../../services/api";

export const useRecipientsSelection = (
  setRecipientsLoading: (loading: boolean) => void,
  setAvailableRecipients: (recipients: any[]) => void,
  setRecipientsTotalCount: (count: number) => void,
  setAllRecipients: (recipients: any[]) => void,
  setSelectedAllFromDB: (selected: boolean) => void,
  setHasLoadedAllRecipients: (loaded: boolean) => void,
  setLoadingMoreRecipients: (loading: boolean) => void,
  allRecipients: any[],
  recipientsCurrentPage: number,
  recipientsItemsPerPage: number,
  selectedRecipientIds: string[],
  setSelectedRecipientIds: (ids: string[]) => void,
  availableRecipients: any[],
  setShowRecipientsModal: (show: boolean) => void,
  setRecipientsCurrentPage: (page: number) => void,
  setSelectedRecipients: (recipients: any[]) => void,
  setShowCreateGroupModal: (show: boolean) => void,
  setShowEditGroupModal: (show: boolean) => void,
  setRecipientsItemsPerPage: (itemsPerPage: number) => void,
  selectedRecipients: any[]
) => {
  const handleSelectRecipients = async () => {
    try {
      setRecipientsLoading(true);

      // Get total count first
      const countResponse = await apiService.getRecipientsFromBackend(
        1,
        1,
        "",
        "ACTIVE"
      );
      const totalCount = (countResponse as any).data?.total || 0;
      setRecipientsTotalCount(totalCount);

      let allRecipientsData: any[] = [];

      if (totalCount <= 1000) {
        // Load all recipients if count is manageable
        const bulkResponse = await apiService.getRecipientsFromBackend(
          1,
          0,
          "",
          "ACTIVE"
        );
        allRecipientsData = (bulkResponse as any).data?.recipients || [];
      } else {
        // Load in chunks for large datasets
        const chunkSize = 1000;
        const totalChunks = Math.ceil(totalCount / chunkSize);

        for (let i = 1; i <= totalChunks; i++) {
          const bulkResponse = await apiService.getRecipientsFromBackend(
            i,
            chunkSize,
            "",
            "ACTIVE"
          );
          const chunkData = (bulkResponse as any).data?.recipients || [];
          allRecipientsData = [...allRecipientsData, ...chunkData];
        }
      }

      // Also load users from the users endpoint
      try {
        const usersResponse = await apiService.getUsers({
          page: 1,
          limit: 1,
          search: "",
          status: "all",
        });
        const usersTotalCount = (usersResponse as any).data?.total || 0;

        if (usersTotalCount > 0) {
          if (usersTotalCount <= 1000) {
            const allUsersResponse = await apiService.getUsers({
              page: 1,
              limit: 0,
              search: "",
              status: "all",
            });
            const usersData = (allUsersResponse as any).data?.users || [];
            allRecipientsData = [...allRecipientsData, ...usersData];
          } else {
            const chunkSize = 1000;
            const totalChunks = Math.ceil(usersTotalCount / chunkSize);

            for (let i = 1; i <= totalChunks; i++) {
              const chunkUsersResponse = await apiService.getUsers({
                page: i,
                limit: chunkSize,
                search: "",
                status: "all",
              });
              const chunkUsersData =
                (chunkUsersResponse as any).data?.users || [];
              allRecipientsData = [...allRecipientsData, ...chunkUsersData];
            }
          }
        }
      } catch (usersError) {
        console.warn("Could not load users:", usersError);
      }

      // Remove duplicates based on email
      const uniqueRecipients = allRecipientsData.reduce(
        (acc: any[], current: any) => {
          const existingIndex = acc.findIndex(
            (item: any) => item.email === current.email
          );
          if (existingIndex === -1) {
            acc.push(current);
          } else {
            // Merge data if user exists in both sources
            acc[existingIndex] = { ...acc[existingIndex], ...current };
          }
          return acc;
        },
        [] as any[]
      );

      setAllRecipients(uniqueRecipients);
      setSelectedAllFromDB(false);
      setHasLoadedAllRecipients(true);

      // Set initial pagination
      const startIndex = (1 - 1) * recipientsItemsPerPage;
      const endIndex = startIndex + recipientsItemsPerPage;
      const paginatedRecipients = uniqueRecipients.slice(startIndex, endIndex);
      setAvailableRecipients(paginatedRecipients);

      setShowRecipientsModal(true);
    } catch (error) {
      console.error("Error loading recipients:", error);
    } finally {
      setRecipientsLoading(false);
    }
  };

  const handleRecipientSelection = (recipient: any, isSelected: boolean) => {
    if (isSelected) {
      setSelectedRecipientIds([...selectedRecipientIds, recipient.id]);
    } else {
      setSelectedRecipientIds(
        selectedRecipientIds.filter((id) => id !== recipient.id)
      );
    }
  };

  const handleRecipientsPageChange = (newPage: number) => {
    setRecipientsCurrentPage(newPage);
    const startIndex = (newPage - 1) * recipientsItemsPerPage;
    const endIndex = startIndex + recipientsItemsPerPage;
    const paginatedRecipients = allRecipients.slice(startIndex, endIndex);
    setAvailableRecipients(paginatedRecipients);
  };

  const handleRecipientsItemsPerPageChange = (newItemsPerPage: number) => {
    setRecipientsItemsPerPage(newItemsPerPage);
    setRecipientsCurrentPage(1);
    const startIndex = (1 - 1) * newItemsPerPage;
    const endIndex = startIndex + newItemsPerPage;
    const paginatedRecipients = allRecipients.slice(startIndex, endIndex);
    setAvailableRecipients(paginatedRecipients);
  };

  const handleSelectAllFromDB = async () => {
    setSelectedAllFromDB(true);
    const activeRecipients = allRecipients.filter(
      (r) => r.status === "ACTIVE" || r.status === undefined
    );
    const allActiveIds = activeRecipients.map((recipient) => recipient.id);
    setSelectedRecipientIds(allActiveIds);
  };

  const handleSelectAllVisible = () => {
    const visibleIds = availableRecipients.map((recipient) => recipient.id);
    const combinedIds = Array.from(
      new Set([...selectedRecipientIds, ...visibleIds])
    );
    const uniqueIds = combinedIds.filter(
      (id, index) => combinedIds.indexOf(id) === index
    );
    setSelectedRecipientIds(uniqueIds);
  };

  const handleClearAllSelections = () => {
    setSelectedRecipientIds([]);
    setSelectedAllFromDB(false);
  };

  const handleLoadAllRecipients = async () => {
    setLoadingMoreRecipients(true);
    try {
      const bulkResponse = await apiService.getRecipientsFromBackend(
        1,
        0,
        "",
        "ACTIVE"
      );
      const recipientsData = (bulkResponse as any).data?.recipients || [];
      setAllRecipients(recipientsData);
      setHasLoadedAllRecipients(true);

      const startIndex = (recipientsCurrentPage - 1) * recipientsItemsPerPage;
      const endIndex = startIndex + recipientsItemsPerPage;
      const paginatedRecipients = recipientsData.slice(startIndex, endIndex);
      setAvailableRecipients(paginatedRecipients);
    } catch (error) {
      console.error("Error loading all recipients:", error);
    } finally {
      setLoadingMoreRecipients(false);
    }
  };

  const handleAddSelectedRecipients = () => {
    const recipientsToAdd = allRecipients.filter((recipient) =>
      selectedRecipientIds.includes(recipient.id)
    );

    // Filter out duplicates
    const newRecipients = recipientsToAdd.filter(
      (newRecipient: any) =>
        !selectedRecipients.some(
          (existing: any) => existing.email === newRecipient.email
        )
    );

    setSelectedRecipients([...selectedRecipients, ...newRecipients]);
    setSelectedRecipientIds([]);
    setSelectedAllFromDB(false);
    setShowRecipientsModal(false);
  };

  return {
    handleSelectRecipients,
    onRecipientSelection: handleRecipientSelection,
    handleRecipientsPageChange,
    handleRecipientsItemsPerPageChange,
    handleSelectAllFromDB,
    handleSelectAllVisible,
    handleClearAllSelections,
    handleLoadAllRecipients,
    handleAddSelectedRecipients,
  };
};
